import { AsyncPipe, Location } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { FormsModule } from "@angular/forms";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { AuthService } from "@core/auth.service";
import { IonicModule, ModalController } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { throwError } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import { TermsAndPolicyComponent } from "src/app/components/terms-and-policy/terms-and-policy.component";
import { CampaignTrackingService } from 'src/app/services/campaign-tracking.service';
import { CommonService } from "src/app/services/common/common.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { InProgressAnimationComponent } from "../../../../components/in-progress-animation/in-progress-animation.component";
import { SafeHtmlPipe } from "../../../../pipe/safehtmlpipe";
import { AccountRepositoryService } from "../../account-repository.service";
@Component({
  selector: "app-preference",
  templateUrl: "./preference.component.html",
  styleUrls: ["./preference.component.scss"],
  standalone: true,
  imports: [
    IonicModule,
    FormsModule,
    InProgressAnimationComponent,
    AsyncPipe,
    SafeHtmlPipe,
    TranslatePipe,
  ],
})
export class PreferenceComponent implements OnInit {
  [x: string]: any;
  selectedLanguage: string = "en";
  private closeAllModals = false;
  public userId: string;
  proSub = localStorage.getItem('proSub');
  smsPreference = localStorage.getItem('smsPreference');
  emailPreference = localStorage.getItem('emailPreference');
  pushPreference = localStorage.getItem('pushPreference');
  flowpathValue = localStorage.getItem('flowPath');
  codeRunCheck: boolean;
  /** Prevents programmatic model updates from triggering Firestore/SQL writes during load. */
  private isHydratingPreferences = true;
  preferencesReady = false;

  userPreferences$ = this.accountService.getUserPreferences().pipe(
    map((userPreferences: any) => {
      if (
        userPreferences &&
        userPreferences.content &&
        userPreferences.content[0] &&
        userPreferences.content[0]?._bottom_text[this.selectedLanguage]
      ) {
        const originalText = userPreferences.content[0]._bottom_text[this.selectedLanguage];

        if (this.flowpathValue && !this.userId) {
          userPreferences.content[0]._bottom_text[this.selectedLanguage] = "We may, on occasion, still need to send you important messages about your account, privacy and security.";
        } else {
          userPreferences.content[0]._bottom_text[this.selectedLanguage] = originalText;
        }
        userPreferences.content[0]._bottom_text[this.selectedLanguage] = userPreferences?.content[0]?._bottom_text[
          this.selectedLanguage
        ].replace("##Privacy Policy##", `<span style="color: #68b6f3;">Privacy Policy</span>`);
      }
      userPreferences?.content[0]?._bottom_text[this.selectedLanguage];
      return userPreferences;
    })
  );

  // Consent-safe default: OFF until a stored value is loaded.
  preferences_settings = {
    email: false,
    sms: false,
    push: false,
  };
  constructor(
    private modalController: ModalController,
    private bottomSheet: MatBottomSheet,
    private accountService: AccountRepositoryService,
    private analytics: AngularFireAnalytics,
    private authService: AuthService,
    public translate: TranslateService,
    private GTMSegment: GTMSegmentService,
    private matDailogRef: MatDialogRef<PreferenceComponent>,
    private location: Location,
    private commonServices: CommonService,
    private campaignTrackingService: CampaignTrackingService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }
  ngOnInit() {
    this.codeRunCheck = false;
    this.GTMSegment.GTMPopupInitialization("PreferencePopup");
    this.authService?.selectedlanguage$?.subscribe((l) => {
      this.selectedLanguage = l ? l : "en";
      if (l) {
        this.translate.use(l ? l : "en");
      }
    });
    this.commonServices.getUid("").pipe(take(1)).subscribe({
      next: (uid) => {
        this.userId = uid;
        this.analytics.logEvent("preference_screen", { userId: uid });
        if (uid) {
          this.loadSavedPreferences();
        } else {
          this.loadGuestPreferencesFromLocalStorage();
        }
      },
      error: () => {
        this.loadGuestPreferencesFromLocalStorage();
      }
    })
    this.accountService.dismissAll$.pipe(take(1)).subscribe((page) => {
      if (page == "legal") {
        this.closeAllModals = true;
      }
    });
  }

  private hasStoredModalPreferences(): boolean {
    return localStorage.getItem('smsPreference') !== null
      || localStorage.getItem('emailPreference') !== null
      || localStorage.getItem('pushPreference') !== null;
  }

  private applyLocalStoragePreferences(): boolean {
    if (!this.hasStoredModalPreferences()) {
      return false;
    }
    this.preferences_settings.sms = localStorage.getItem('smsPreference') === 'true';
    this.preferences_settings.email = localStorage.getItem('emailPreference') === 'true';
    this.preferences_settings.push = localStorage.getItem('pushPreference') === 'true';
    return true;
  }

  private writeModalPreferencesToLocalStorage(): void {
    localStorage.setItem('smsPreference', this.preferences_settings.sms.toString());
    localStorage.setItem('emailPreference', this.preferences_settings.email.toString());
    localStorage.setItem('pushPreference', this.preferences_settings.push.toString());
  }

  private loadGuestPreferencesFromLocalStorage(): void {
    this.isHydratingPreferences = true;
    if (!this.applyLocalStoragePreferences()) {
      this.preferences_settings.sms = false;
      this.preferences_settings.email = false;
      this.preferences_settings.push = false;
    }
    this.finishPreferenceHydration();
  }

  private loadSavedPreferences(): void {
    this.isHydratingPreferences = true;
    // UAT keeps the last toggle choice in localStorage and never waits on Firestore
    // for the UI. Cloud-function saves do not update the local cache in time, so a
    // server re-read on every visit showed the previous (usually off) state.
    if (this.applyLocalStoragePreferences()) {
      this.finishPreferenceHydration();
      return;
    }
    this.accountService.getUserSpecificPreferences().pipe(take(1)).subscribe({
      next: (userPref) => {
        const saved = userPref?.preferences_settings;
        if (saved) {
          (['sms', 'email', 'push'] as const).forEach((key) => {
            if (typeof saved[key] === 'boolean') {
              this.preferences_settings[key] = saved[key];
            }
          });
        } else {
          this.preferences_settings.sms = false;
          this.preferences_settings.email = false;
          this.preferences_settings.push = false;
        }
        this.writeModalPreferencesToLocalStorage();
        this.finishPreferenceHydration();
      },
      error: (e) => {
        console.error('Error loading preferences', e);
        this.loadGuestPreferencesFromLocalStorage();
      }
    });
  }



  goBack() {
    const flowpathValue = localStorage.getItem('flowPath');
    if (flowpathValue && !this.userId) {
      this.bottomSheet.dismiss();
      return;
    }
    if (location.href?.indexOf("navigate=preference") > -1) {
      this.location.back();
    }
    this.matDailogRef.close();
  }

  openPrefModal(event, prefdetail) {
    if (!prefdetail || !prefdetail.content[1]) return;
    const data = [
      "Privacy Policy",
      "Datenschutzerklärung",
      "Política de privacidad",
      "Politique de confidentialité",
      "Politica sulla riservatezza",
    ];
    const clickedText = event.target.innerText;
    if (data.some((f) => f?.toLowerCase() === clickedText?.toLowerCase())) {
      this.showTermsPolicy("privacy_policy");

    }
  }

  async showTermsPolicy(contentType: string) {
    const showTermsAndCondition = this.bottomSheet.open(TermsAndPolicyComponent, {
      panelClass: "terms-dialog",
      data: { contentType: contentType },
    });
    showTermsAndCondition.afterDismissed().subscribe(() => {
      this.matDailogRef.close();
    });
  }
  changePrefStatus(event: boolean | { detail?: { checked?: boolean } }, context: string) {
    if (this.isHydratingPreferences) {
      return;
    }
    const checked = this.coerceToggleValue(event);
    this.preferences_settings[context] = checked;
    this.writeModalPreferencesToLocalStorage();

    if (!this.userId) {
      localStorage.setItem('pendingGuestMarketingPrefs', 'true');
      return;
    }

    // Explicit Preferences edits supersede signup staging. Keep sms/email/push
    // keys so the UI matches UAT when returning to this page; do not mark them
    // as pending guest prefs, or Home would write them back as registration data.
    localStorage.removeItem('preferenceTick');
    localStorage.removeItem('pendingGuestMarketingPrefs');
    const preference = {
      sms: this.preferences_settings.sms,
      email: this.preferences_settings.email,
      push: this.preferences_settings.push,
    };
    if (context === 'push') {
      this.accountService.saveOneSignalDetailsOption(checked);
    }
    this.accountService
      .saveUserSpecificPreferences({ preferences_settings: preference })
      .subscribe({
        error: (e) => console.error('Error saving preferences', e),
      });
    this.updateGDPRUserPreferences(preference.sms, preference.email, preference.push);
  }

  private coerceToggleValue(event: boolean | { detail?: { checked?: boolean } }): boolean {
    if (typeof event === 'boolean') {
      return event;
    }
    return event?.detail?.checked === true;
  }

  private finishPreferenceHydration(): void {
    this.preferencesReady = true;
    // ion-toggle emits a synthetic change when first rendered; wait one macrotask
    // so that event cannot persist the pre-render value.
    setTimeout(() => {
      this.isHydratingPreferences = false;
    });
  }


  updateGDPRUserPreferences(smsPref: boolean, emailPref: boolean, pushPref: boolean) {
    this.accountService
      .updateGDPRUserPreferences(smsPref, emailPref, pushPref)
      .pipe(
        catchError((error) => {
          return throwError(`An error occurred while updating preferences ${error}`);
        })
      )
      .subscribe(
        (res) => {
          localStorage.setItem('preference_updated', "true");
          const unsubscribe_session_id = localStorage.getItem('unsubscribe_session_id')??null;
          this.authService.getV2CloudFunction("unsubscribevisitorlogv2", {
              module_name: "update_customer_promotional_prefs",
              sql_customer_id: this.userId,
              session_id: unsubscribe_session_id
            }).subscribe();
          if (res && res?.response && res?.response?.status === "success") {
          }
        },
        (err) => {
          return err;
        }
      );
  }

  ngOnDestroy() {
    if (localStorage.getItem('preference_updated') === "true") {
      this.campaignTrackingService.clearCampaignIds();
    }
  }
}
